import { useState } from "react";
import { Switch, Route, Link, useRouteMatch } from "react-router-dom";

import {
  Box,
  Typography,
  CssBaseline,
  Divider,
  ButtonBase,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Tooltip,
  IconButton,
  useTheme
} from "@mui/material";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHome, faIndustry, faScaleBalanced } from "@fortawesome/free-solid-svg-icons";
import { faPodiumStar } from "@fortawesome/pro-solid-svg-icons";

import { AnimatedLogo } from "@aclymatepackages/atoms";

import MuiLink from "../../atoms/mui/Link";

import InternalAdminContextProvider, {
  useInternalAdminContext
} from "../../../helpers/contexts/internalAdmin";
import EventReportingPage from "./EventReportingPage";
import AdminVendorNormalizationPage from "./AdminVendorNormalizationPage";
import AdminRollupsV2ComparisonPage from "./AdminRollupsV2ComparisonPage";

const adminNavItems = [
  {
    name: "Event Reporting",
    description:
      "Upload event attendee CSVs and calculate carbon emissions per attendee.",
    icon: faPodiumStar,
    path: "event-reporting"
  },
  {
    name: "Vendor Normalization",
    description:
      "Review and merge duplicate vendors identified in the global vendor database.",
    icon: faIndustry,
    path: "vendor-normalization"
  },
  {
    name: "Rollups V2 Comparison",
    description:
      "Compare one company's rollups-v2 shadow buckets against legacy and a live recompute.",
    icon: faScaleBalanced,
    path: "rollups-v2-comparison"
  }
];

const OpenNavLink = ({ name, icon, path }) => {
  const { palette } = useTheme();

  return (
    <Grid item>
      <ButtonBase href={path} id={`${name.replace(" ", "")}Btn`}>
        <Grid container spacing={2} flexWrap="nowrap" alignItems="center">
          <Grid item>
            <Box
              style={{ width: "32px" }}
              display="flex"
              justifyContent="center"
              p={1}
            >
              <FontAwesomeIcon
                icon={icon}
                size="2x"
                style={{ color: palette.primary.main }}
              />
            </Box>
          </Grid>
          <Grid item>
            <Typography variant="h5" noWrap color="primary">
              {name}
            </Typography>
          </Grid>
        </Grid>
      </ButtonBase>
    </Grid>
  );
};

const ClosedNavLink = ({ name, icon, path }) => (
  <Grid item>
    <Tooltip title={name}>
      <span>
        <MuiLink href={path}>
          <IconButton
            color="primary"
            id={`${name.replace(" ", "")}Btn`}
            size="small"
          >
            <FontAwesomeIcon icon={icon} size="2x" />
          </IconButton>
        </MuiLink>
      </span>
    </Tooltip>
  </Grid>
);

const AdminSideNav = ({ sideNavOpen, setSideNavOpen }) => {
  const { url } = useRouteMatch();

  const toggleSideNav = () =>
    setSideNavOpen((open) => {
      window.localStorage.setItem("adminSideNavOpen", !open);
      return !open;
    });

  const navItems = [
    { name: "Home", icon: faHome, path: "" },
    ...adminNavItems.map(({ name, icon, path }) => ({
      name,
      icon,
      path
    }))
  ];

  return (
    <Box
      display="flex"
      flexDirection="column"
      style={{
        height: "100vh",
        background: "white",
        width: sideNavOpen ? "280px" : "72px",
        minWidth: sideNavOpen ? "280px" : "72px",
        transition: "width 0.3s ease, min-width 0.3s ease",
        overflow: "hidden"
      }}
    >
      <Box
        p={1}
        style={{
          boxSizing: "content-box",
          height: "56px"
        }}
        display="flex"
        alignItems="center"
        justifyContent={sideNavOpen ? "flex-start" : "center"}
      >
        {sideNavOpen ? (
          <img
            src="/images/aclymate-full-logo.png"
            alt="Aclymate logo"
            style={{ maxWidth: "260px" }}
          />
        ) : (
          <img
            src="/images/aclymate-icon.png"
            alt="Aclymate logo"
            style={{ width: "56px" }}
          />
        )}
      </Box>
      <Divider />
      <Box
        flexGrow={1}
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box p={2}>
          <Grid
            container
            direction="column"
            spacing={2}
            align={sideNavOpen ? "flex-start" : "center"}
          >
            {navItems.map(({ name, icon, path }, idx) => {
              const fullPath = path === "" ? url : `${url}/${path}`;

              return sideNavOpen ? (
                <OpenNavLink
                  path={fullPath}
                  name={name}
                  icon={icon}
                  key={`admin-nav-open-${idx}`}
                />
              ) : (
                <ClosedNavLink
                  path={fullPath}
                  name={name}
                  icon={icon}
                  key={`admin-nav-closed-${idx}`}
                />
              );
            })}
          </Grid>
        </Box>
        <div />
      </Box>
      <Divider />
      <Box display="flex" justifyContent="center">
        <IconButton onClick={toggleSideNav} size="large">
          {sideNavOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </Box>
    </Box>
  );
};

const AdminHome = () => {
  const { url } = useRouteMatch();

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Admin Tools
      </Typography>
      <Grid container spacing={3}>
        {adminNavItems.map(({ name, description, icon, path }) => (
          <Grid item xs={12} sm={6} md={4} key={path}>
            <Card>
              <CardActionArea component={Link} to={`${url}/${path}`}>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <FontAwesomeIcon icon={icon} style={{ marginRight: 8 }} />
                    <Typography variant="h6">{name}</Typography>
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    {description}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

const AdminLayout = () => {
  const { path } = useRouteMatch();
  const LSsideNavOpen = window.localStorage.getItem("adminSideNavOpen");
  const [sideNavOpen, setSideNavOpen] = useState(LSsideNavOpen !== "false");

  return (
    <>
      <CssBaseline />
      <Box
        display="flex"
        style={{ height: "100vh", overflow: "hidden" }}
        alignItems="stretch"
      >
        <AdminSideNav
          sideNavOpen={sideNavOpen}
          setSideNavOpen={setSideNavOpen}
        />
        <Box
          flexGrow={1}
          style={{ overflow: "hidden", height: "100%" }}
          display="flex"
          flexDirection="column"
        >
          <Box flexGrow={1} style={{ overflowY: "auto" }} p={4}>
            <Switch>
              <Route exact path={path}>
                <AdminHome />
              </Route>
              <Route path={`${path}/event-reporting`}>
                <EventReportingPage />
              </Route>
              <Route path={`${path}/vendor-normalization`}>
                <AdminVendorNormalizationPage />
              </Route>
              <Route path={`${path}/rollups-v2-comparison`}>
                <AdminRollupsV2ComparisonPage />
              </Route>
            </Switch>
          </Box>
        </Box>
      </Box>
    </>
  );
};

const AdminContent = () => {
  const { user, userLoading, isAdminCheckLoading, isUserAclymateAdmin } =
    useInternalAdminContext();

  if (userLoading || isAdminCheckLoading) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        style={{ height: "100vh" }}
      >
        <AnimatedLogo />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box p={4}>
        <Typography variant="h5">
          Please log in to access admin tools.
        </Typography>
      </Box>
    );
  }

  if (!isUserAclymateAdmin) {
    return (
      <Box p={4}>
        <Typography variant="h5">Access Denied</Typography>
        <Typography variant="body1" color="textSecondary">
          You do not have admin privileges.
        </Typography>
      </Box>
    );
  }

  return <AdminLayout />;
};

const AdminPage = () => (
  <InternalAdminContextProvider>
    <AdminContent />
  </InternalAdminContextProvider>
);

export default AdminPage;
